import React, { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import AdminHeaderNew from '../../NewAdminPanel/Common/AdminHeader';
import AdminFooterNew from '../../NewAdminPanel/Common/AdminFooter';
import AdminSideBarNew from '../../NewAdminPanel/Common/AdminSidebarMnt';

const containerStyle = {
  width: '100%',
  height: '50vh',
};

const defaultCenter = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

const apiBaseUrl =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_DEV_MODE
    : process.env.REACT_APP_PRO_MODE;

const parseCoordinate = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getPolygonShapeFromData = (data) => {
  if (!data || typeof data !== 'object') return null;
  return (
    data.strPolygonShape ||
    data.polygonShape ||
    data.polygonshape ||
    data.strpolygonshape ||
    null
  );
};

const normalizePolygonCoordinates = (rawPolygonShape) => {
  if (!rawPolygonShape) return [];

  let parsed = rawPolygonShape;
  if (typeof rawPolygonShape === 'string') {
    try {
      parsed = JSON.parse(rawPolygonShape);
    } catch (error) {
      return [];
    }
  }

  const points = Array.isArray(parsed?.[0]) ? parsed[0] : parsed;
  if (!Array.isArray(points)) return [];

  return points
    .map((point) => {
      const lat = parseCoordinate(point?.lat ?? point?.latitude);
      const lng = parseCoordinate(point?.lng ?? point?.longitude ?? point?.long);
      if (lat === null || lng === null) return null;
      return { lat, lng };
    })
    .filter(Boolean);
};

const buildImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return null;

  const trimmedPath = imagePath.trim();
  if (!trimmedPath) return null;

  if (
    trimmedPath.toLowerCase().startsWith('http://') ||
    trimmedPath.toLowerCase().startsWith('https://') ||
    trimmedPath.startsWith('//')
  ) {
    return trimmedPath;
  }

  const baseUrl = apiBaseUrl?.replace(/\/+$/, '');
  if (!baseUrl) return trimmedPath;

  const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  return `${baseUrl}/api/common/GetAttachbyURL?imageURL=${encodeURIComponent(normalizedPath)}`;
};

const normalizeExpImageData = (rawImageData) => {
  if (!rawImageData) return [];

  const rows = Array.isArray(rawImageData) ? rawImageData : [rawImageData];
  const normalized = [];

  rows.forEach((row, rowIndex) => {
    if (!row) return;

    const pathValue =
      row.imagePath ||
      row.imgPath ||
      row.imagepath ||
      row.attPath ||
      row.imageName ||
      row.imagename;

    if (!pathValue) return;

    normalized.push({
      ...row,
      _pathIndex: rowIndex,
      resolvedImageSrc: buildImageUrl(pathValue),
      latitude: row.surveyorLat ?? row.latitude ?? row.imageLat,
      longitude: row.surveyorLong ?? row.longitude ?? row.imageLong,
    });
  });

  const seenSrc = new Set();
  return normalized.filter((img) => {
    if (!img.resolvedImageSrc || seenSrc.has(img.resolvedImageSrc)) return false;
    seenSrc.add(img.resolvedImageSrc);
    return true;
  });
};

const flattenImplementData = (dataContent) => {
  if (!dataContent) return [];

  const rows = Array.isArray(dataContent) ? dataContent : [dataContent];
  const flattened = [];

  rows.forEach((item) => {
    if (item?.implements && Array.isArray(item.implements)) {
      item.implements.forEach((implement) => {
        flattened.push({
          categoryName: item.categoryName || item.CategoryName || 'N/A',
          implementName: implement.implementName || implement.ImplementName || 'N/A',
          implementCount: implement.implementCount ?? 'N/A',
          isWorking: implement.isWorking,
          areaCovered: implement.areaCovered,
        });
      });
      return;
    }

    flattened.push({
      categoryName: item.categoryName || item.CategoryName || 'N/A',
      implementName:
        item.implementName ||
        item.ImplementName ||
        item.ImplementSubsidyName ||
        'N/A',
      implementCount: item.implementCount ?? 'N/A',
      isWorking: item.isWorking,
      areaCovered: item.areaCovered,
    });
  });

  return flattened;
};

const ExpMachineryDetailView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    beneficiaryId: stateBeneficiaryId,
    farmerData: initialFarmerData,
    returnTo,
    expandedRows,
    expandedSurveyorRows,
    surveyorDetailsByDistrict,
    expBenBySurveyor,
    farmerDetailsBySurveyor,
  } = location.state || {};

  const beneficiaryId =
    stateBeneficiaryId ??
    initialFarmerData?.beneficiaryId ??
    initialFarmerData?.beneficiaryID ??
    initialFarmerData?.id ??
    initialFarmerData?.Id;

  const initialPolygonShape = getPolygonShapeFromData(initialFarmerData);

  const [beneficiaryData, setBeneficiaryData] = useState(initialFarmerData || null);
  const [imageData, setImageData] = useState([]);
  const [implementData, setImplementData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState();

  const polygonShape = getPolygonShapeFromData(beneficiaryData) || initialPolygonShape;
  const polygonCoordinates = useMemo(
    () => normalizePolygonCoordinates(polygonShape),
    [polygonShape]
  );

  const fallbackLat = parseCoordinate(
    beneficiaryData?.surveyorLat ??
      beneficiaryData?.imageLat ??
      initialFarmerData?.surveyorLat ??
      initialFarmerData?.imageLat
  );
  const fallbackLng = parseCoordinate(
    beneficiaryData?.surveyorLong ??
      beneficiaryData?.imageLong ??
      initialFarmerData?.surveyorLong ??
      initialFarmerData?.imageLong
  );
  const hasPolygonShape = polygonCoordinates.length > 0;
  const hasMapLocation = hasPolygonShape || (fallbackLat !== null && fallbackLng !== null);
  const mapCenter =
    fallbackLat !== null && fallbackLng !== null
      ? { lat: fallbackLat, lng: fallbackLng }
      : defaultCenter;

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script-exp-machinery',
    googleMapsApiKey: 'AIzaSyD4X4kzlpReymgcXUXGh0Q3TalmxJmasM0',
  });

  const onLoad = useCallback(
    (mapInstance) => {
      if (!window.google?.maps) return;

      if (polygonCoordinates.length > 0) {
        try {
          const polygon = new window.google.maps.Polygon({
            paths: polygonCoordinates,
            strokeColor: '#FF0000',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.1,
          });
          polygon.setMap(mapInstance);

          const bounds = new window.google.maps.LatLngBounds();
          polygonCoordinates.forEach((coord) => {
            bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
          });
          mapInstance.fitBounds(bounds);

          new window.google.maps.Marker({
            position: bounds.getCenter(),
            map: mapInstance,
            title: 'Beneficiary Location',
          });
        } catch (error) {
          console.error('Error creating polygon:', error);
        }
      } else if (fallbackLat !== null && fallbackLng !== null) {
        mapInstance.setCenter({ lat: fallbackLat, lng: fallbackLng });
        mapInstance.setZoom(18);
        new window.google.maps.Marker({
          position: { lat: fallbackLat, lng: fallbackLng },
          map: mapInstance,
          title: 'Beneficiary Location',
        });
      }
    },
    [polygonCoordinates, fallbackLat, fallbackLng]
  );

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo, {
        state: {
          expandedRows,
          expandedSurveyorRows,
          surveyorDetailsByDistrict,
          expBenBySurveyor: expBenBySurveyor || farmerDetailsBySurveyor,
          farmerDetailsBySurveyor,
          restoreState: true,
        },
      });
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!beneficiaryId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const parsedBeneficiaryId = parseInt(beneficiaryId, 10);

        const [beneficiaryResponse, imageResponse, implementResponse] = await Promise.all([
          axios.post(
            `${apiBaseUrl}/api/ExpensiveMachinery/GetExpBenDataByBenID`,
            { beneficiaryId: parsedBeneficiaryId },
            { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
          ),
          axios.post(
            `${apiBaseUrl}/api/ExpensiveMachinery/GetExpImageData`,
            { beneficiaryId: parsedBeneficiaryId },
            { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
          ),
          axios.post(
            `${apiBaseUrl}/api/ExpensiveMachinery/GetExpImplemnetData`,
            { beneficiaryId: parsedBeneficiaryId },
            { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
          ),
        ]);

        if (beneficiaryResponse.data?.success && beneficiaryResponse.data?.dataContent) {
          const content = Array.isArray(beneficiaryResponse.data.dataContent)
            ? beneficiaryResponse.data.dataContent[0]
            : beneficiaryResponse.data.dataContent;

          setBeneficiaryData({
            ...(initialFarmerData || {}),
            ...(content || {}),
            polygonShape:
              getPolygonShapeFromData(content) ||
              getPolygonShapeFromData(initialFarmerData),
          });
        } else {
          setBeneficiaryData(initialFarmerData || null);
        }

        if (imageResponse.data?.success && Array.isArray(imageResponse.data.dataContent)) {
          setImageData(normalizeExpImageData(imageResponse.data.dataContent));
        } else {
          setImageData([]);
        }

        if (implementResponse.data?.success && implementResponse.data.dataContent) {
          setImplementData(flattenImplementData(implementResponse.data.dataContent));
        } else {
          setImplementData([]);
        }
      } catch (error) {
        console.error('Error fetching expensive machinery details:', error);
        setBeneficiaryData(initialFarmerData || null);
        setImageData([]);
        setImplementData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [beneficiaryId, initialFarmerData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminHeaderNew />
      <AdminSideBarNew />
      <main id="main" className="main">
        <div className="pagetitle">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Individual Details</h1>
              <nav>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">Home</li>
                  <li className="breadcrumb-item active">Individual Details</li>
                </ol>
              </nav>
            </div>
            <button type="button" className="btn btn-success" onClick={handleBack}>
              <i>
                <FontAwesomeIcon icon={faArrowLeft} />
              </i>{' '}
              Back
            </button>
          </div>
        </div>

        <section className="section">
          <div className="row">
            <div className="col-lg-12 dashboard">
              <div className="card">
                <div className="card-body p-5 pt-2">
                  {beneficiaryData ? (
                    <>
                      {hasMapLocation && (
                        <div className="row cropview">
                          <h5 className="formgrouping">Location Map</h5>
                          {isLoaded ? (
                            <GoogleMap
                              mapContainerStyle={containerStyle}
                              center={mapCenter}
                              zoom={18}
                              onLoad={onLoad}
                              mapTypeId="satellite"
                            />
                          ) : loadError ? (
                            <div className="alert alert-warning" style={{ height: '50vh' }}>
                              <h6>Map Loading Error</h6>
                              <p>Unable to load Google Maps. Please check your internet connection.</p>
                              <small>Error: {loadError.message}</small>
                            </div>
                          ) : (
                            <div
                              className="d-flex justify-content-center align-items-center"
                              style={{ height: '50vh' }}
                            >
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading Map...</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="row cropview">
                        <h5 className="formgrouping">Beneficiary Basic Information</h5>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Farmer Name</h6>
                            <p>{beneficiaryData.farmerName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Contact Person</h6>
                            <p>{beneficiaryData.contactPersonName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Mobile Number</h6>
                            <p>{beneficiaryData.mobileNumber || beneficiaryData.mobileNo || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Financial Year</h6>
                            <p>{beneficiaryData.financialYear || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Aadhaar No.</h6>
                            <p>{beneficiaryData.aadharNo || beneficiaryData.aadhaarNo || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Gender</h6>
                            <p>{beneficiaryData.genderName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Farmer Type</h6>
                            <p>{beneficiaryData.farmerTypeName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Beneficiary ID</h6>
                            <p>{beneficiaryData.beneficiaryId || beneficiaryId || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="row cropview">
                        <h5 className="formgrouping">Location Details</h5>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>State</h6>
                            <p>{beneficiaryData.stateName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>District</h6>
                            <p>{beneficiaryData.districtName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Sub-District</h6>
                            <p>
                              {beneficiaryData.subdistrictName ||
                                beneficiaryData.subDistrictName ||
                                'N/A'}
                            </p>
                          </div>
                          <div className="col-md-3">
                            <h6>Village</h6>
                            <p>{beneficiaryData.villageName || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row surveydetailsbg">
                          <div className="col-md-6">
                            <h6>Landmark Address</h6>
                            <p>{beneficiaryData.landMarkAddress || beneficiaryData.fAddress || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Area Type</h6>
                            <p>
                              {beneficiaryData.areaType === '1' || beneficiaryData.areaType === 1
                                ? 'Rural'
                                : beneficiaryData.areaType === '2' || beneficiaryData.areaType === 2
                                  ? 'Urban'
                                  : 'N/A'}
                            </p>
                          </div>
                          <div className="col-md-3">
                            <h6>Surveyor Location</h6>
                            <p>
                              Lat: {fallbackLat ?? 'N/A'}, Long: {fallbackLng ?? 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {(beneficiaryData.establishedByName ||
                        beneficiaryData.year ||
                        beneficiaryData.monthName ||
                        beneficiaryData.schemeName) && (
                        <div className="row cropview">
                          <h5 className="formgrouping">Additional Details</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-3">
                              <h6>Established By</h6>
                              <p>{beneficiaryData.establishedByName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Establishment Year</h6>
                              <p>{beneficiaryData.year || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Establishment Month</h6>
                              <p>{beneficiaryData.monthName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Scheme</h6>
                              <p>{beneficiaryData.schemeName || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {polygonShape && beneficiaryData.polygonArea && (
                        <div className="row cropview">
                          <h5 className="formgrouping">Area Details</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-6">
                              <h6>Polygon Area (sq meters)</h6>
                              <p>{beneficiaryData.polygonArea || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                              <h6>Area in Acres</h6>
                              <p>
                                {beneficiaryData.polygonArea
                                  ? `${(beneficiaryData.polygonArea * 0.000247105).toFixed(4)} acres`
                                  : 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {implementData.length > 0 && (
                        <div className="row cropview">
                          <h5 className="formgrouping">Implement Details</h5>
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead className="table-light">
                                <tr>
                                  <th>Sr. No.</th>
                                  <th>Category Name</th>
                                  <th>Implement Name</th>
                                  <th>Count</th>
                                  <th>Working</th>
                                  <th>Area Covered</th>
                                </tr>
                              </thead>
                              <tbody>
                                {implementData.map((item, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.categoryName}</td>
                                    <td>{item.implementName}</td>
                                    <td>{item.implementCount ?? 'N/A'}</td>
                                    <td>
                                      {item.isWorking === true
                                        ? 'Yes'
                                        : item.isWorking === false
                                          ? 'No'
                                          : 'N/A'}
                                    </td>
                                    <td>{item.areaCovered ?? 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {imageData.length > 0 && (
                        <div className="row cropview">
                          <h5 className="formgrouping">Image Details</h5>
                          {imageData.map((image, index) => (
                            <Fragment key={index}>
                              <div className="col-md-2">
                                <img
                                  src={image.resolvedImageSrc}
                                  alt={`Individual Image ${index + 1}`}
                                  data-bs-toggle="modal"
                                  data-bs-target="#expMachineryImageModal"
                                  className="img-fluid imgdetials"
                                  onClick={() => setActiveImage(index)}
                                  style={{ cursor: 'pointer' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                              <div className="col-md-4">
                                <div className="row imgdetialsbg">
                                  <div className="col-md-6">
                                    <h6>Latitude</h6>
                                    <p>{image.latitude ?? 'N/A'}</p>
                                  </div>
                                  <div className="col-md-6">
                                    <h6>Longitude</h6>
                                    <p>{image.longitude ?? 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </Fragment>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="alert alert-warning">
                      <h5>Beneficiary Not Found</h5>
                      <p>
                        No data available for Beneficiary ID:{' '}
                        <strong>{beneficiaryId || 'N/A'}</strong>
                      </p>
                      <button className="btn btn-primary" onClick={handleBack}>
                        Go Back
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <AdminFooterNew />

      {imageData.length > 0 && (
        <div
          className="modal fade"
          id="expMachineryImageModal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="expMachineryImageModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="expMachineryImageModalLabel">
                  Individual Image
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                  X
                </button>
              </div>
              <div className="modal-body">
                {activeImage !== undefined && imageData[activeImage] && (
                  <img
                    src={imageData[activeImage].resolvedImageSrc}
                    alt={`Individual Image ${activeImage + 1}`}
                    className="img-fluid w-100"
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpMachineryDetailView;
